import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import React, { useMemo } from 'react';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { DisparityOpportunityTitle } from 'apps/sigma2-ui/src/components/DisparityOpportunityTitle';
import { DisparityOpportunityDescription } from 'apps/sigma2-ui/src/components/DisparityOpportunityDescription';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DisparityBarChartStack } from 'apps/sigma2-ui/src/components/DisparityBarChartStack';
import { getDeepDiveNumberOfSubjectComponents } from 'libs/end-to-end/src/deep-dive-section-utils';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';

export interface DeepDiveOverviewCardProps {
  treatmentGroupLevelDefinition: LevelDefinition;
  referenceGroupLevelDefinition: LevelDefinition;
  metricDefinition: MyMetricDefinition;
  rawDisparityPercentage: number;
  controlledDisparityPercentage: number;
  debiasedDisparityPercentage: number | null;
  confidencePercentage: number;
  numberOfTreatmentGroupSubjects: number | null;
  numeratorOfTreatmentGroupSubjects: number | null;
  numberOfReferenceGroupSubjects: number | null;
  numeratorOfReferenceGroupSubjects: number | null;
  controlVariableDisplayNames: string[];
  calibrationFraming: CalibrationFraming;
}

export const DeepDiveOverviewCard = (props: DeepDiveOverviewCardProps) => {
  const {
    treatmentGroupLevelDefinition,
    referenceGroupLevelDefinition,
    metricDefinition,
    rawDisparityPercentage,
    controlledDisparityPercentage,
    debiasedDisparityPercentage,
    controlVariableDisplayNames,
    calibrationFraming,
  } = useMemo(() => {
    return props;
  }, [props]);

  const language = useLanguage();

  const numberOfSubjectComponents = useMemo(() => {
    return getDeepDiveNumberOfSubjectComponents({
      metricDefinition,
      numberOfTreatmentGroupSubjects: props.numberOfTreatmentGroupSubjects,
      numberOfReferenceGroupSubjects: props.numberOfReferenceGroupSubjects,
      referenceGroupLevelDefinition,
      treatmentGroupLevelDefinition,
      numeratorOfTreatmentGroupSubjects:
        props.numeratorOfTreatmentGroupSubjects,
      numeratorOfReferenceGroupSubjects:
        props.numeratorOfReferenceGroupSubjects,
    });
  }, [
    props,
    referenceGroupLevelDefinition,
    metricDefinition,
    treatmentGroupLevelDefinition,
  ]);

  return (
    <Card paddingClassName="p-10" className="h-full w-full">
      <Spacing size={'md'} direction={'vertical'} className="w-full">
        <div className="flex justify-center">
          <DisparityBarChartStack
            rawDisparity={rawDisparityPercentage}
            controlledDisparity={controlledDisparityPercentage}
            debiasedDisparity={debiasedDisparityPercentage}
            calibrationFraming={calibrationFraming}
            variant={'large'}
            hasRunBiasTasks={metricDefinition.hasBiasTestTasks}
          />
        </div>

        <DisparityOpportunityTitle
          treatmentGroup={treatmentGroupLevelDefinition}
          metricDefinition={metricDefinition}
          typographyVariant={'headline-5-alt'}
        />

        <Spacing size={'md'} direction={'vertical'}>
          <DisparityOpportunityDescription
            treatmentGroup={treatmentGroupLevelDefinition}
            referenceGroup={referenceGroupLevelDefinition}
            metricDefinition={metricDefinition}
            rawDisparityPercentage={rawDisparityPercentage}
            controlledDisparityPercentage={controlledDisparityPercentage}
            debiasedDisparityPercentage={debiasedDisparityPercentage}
            controlVariableDisplayNames={controlVariableDisplayNames}
            calibrationFraming={calibrationFraming}
            hasRunBiasTasks={metricDefinition.hasBiasTestTasks}
          />

          {!metricDefinition.hasBiasTestTasks && (
            <LightTooltip
              followCursor={true}
              title={
                <S2Typography
                  translatedContent={language.whyNoBiasOrInefficiencyDetectionTooltip(
                    {
                      calibrationFraming,
                    },
                  )}
                  variant={'body-7'}
                />
              }
            >
              <sup className={'pl-[2px]'}>
                <S2Typography
                  translatedContent={language.whyNoBiasOrInefficiencyDetection({
                    calibrationFraming,
                  })}
                  variant={'headline-7-alt'}
                  textColorVariant="royal-700"
                />
              </sup>
            </LightTooltip>
          )}

          <S2Typography
            translatedContent={language.deepDiveConfidenceText({
              confidencePercentage: props.confidencePercentage,
            })}
            variant={'headline-7-alt'}
          />

          <Box as={'div'} w={'full'} minH="min-h-[1px]" bg="gray-200" />

          {numberOfSubjectComponents.map((component, index) => {
            return (
              <Spacing size={'md'} direction={'vertical'} key={index}>
                <Spacing size={'sm'} direction={'vertical'}>
                  <S2Typography
                    translatedContent={
                      metricDefinition.isMetricBoolean && component.numerator
                        ? language.numeratorOutOfTotal({
                            numeratorSize: component.numerator,
                            sampleSize: component.numberOfSubjects,
                          })
                        : language.dangerousUserInput({
                            value: component.numberOfSubjects,
                          })
                    }
                    variant={'headline-4-alt'}
                  />

                  <S2Typography
                    translatedContent={language.dangerousUserInput({
                      value: component.descriptorText,
                    })}
                    variant={'headline-6-alt'}
                  />
                </Spacing>

                <Box as={'div'} w={'full'} minH="min-h-[1px]" bg="gray-200" />
              </Spacing>
            );
          })}
        </Spacing>
      </Spacing>
    </Card>
  );
};
